import { useAuth0 } from '@auth0/auth0-react';
import { CalendarX, ShieldOff, UserX } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { StatePanel } from '@/components/ui/StatePanel';
import { apiErrorReason } from '@/lib/api';
import { env } from '@/lib/env';
import { useMe } from '@/lib/me';

/** Bloqueos de cuenta que cortan la app entera (`/me` → 403 con esa razón). */
const BLOCKS = new Map<string, { icon: ReactElement; title: string; body: string }>([
  [
    'user_inactive',
    {
      icon: <UserX />,
      title: 'Tu acceso al QMS está desactivado',
      body: 'Un administrador de calidad desactivó tu usuario. Si crees que es un error, comunícate con el departamento de Calidad para que lo reactive.',
    },
  ],
  [
    'no_access',
    {
      icon: <ShieldOff />,
      title: 'Aún no tienes acceso al QMS',
      body: 'Tu usuario quedó registrado, pero todavía no tiene un rol asignado. Pide al departamento de Calidad que te asigne el rol que corresponde y vuelve a entrar.',
    },
  ],
  [
    'provider_access_expired',
    {
      icon: <CalendarX />,
      title: 'Tu acceso temporal al QMS terminó',
      body: 'El acceso que Grupo DIM te otorgó venció o fue retirado. Si todavía lo necesitas, solicita un nuevo acceso a tu contacto de Calidad en Grupo DIM.',
    },
  ],
]);

/** Corta la app entera si la cuenta no puede usar el QMS (desactivada, sin rol ni
 *  acceso, o proveedor externo sin acceso vigente): un mensaje claro y la salida, en vez de cada pantalla
 *  culpando a la conexión. */
export function AccountGate({ children }: { children: ReactNode }): ReactElement {
  const me = useMe();
  const { logout } = useAuth0();
  const block = me.isError ? BLOCKS.get(apiErrorReason(me.error) ?? '') : undefined;

  if (block) {
    return (
      <main className="main" style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <StatePanel icon={block.icon} title={block.title} role="alert">
          {block.body}{' '}
          <button
            type="button"
            className="btn sm mut"
            onClick={() =>
              void logout({ logoutParams: { returnTo: env.VITE_AUTH0_LOGOUT_RETURN_TO } })
            }
          >
            Cerrar sesión
          </button>
        </StatePanel>
      </main>
    );
  }
  return <>{children}</>;
}
